import React, { useMemo, useState } from "react";
import { useGetDataSourceList } from "@via/pages/data/data-connections/data-connections.api.tsx";
import { DataSource, DataSourceProvider } from "common";
import { TruncatedText } from "@via/components/truncated-text.tsx";
import ClusterServerLogo from "/cluster-server-logo.svg";
import { VerticalTabs } from "@via/components/tabs.tsx";
import S3Logo from "/aws-s3-logo.svg";
import GCSLogo from "/gcs-logo.svg";
import DeleteBookmarkItemModal from "@via/pages/data/data-connections/bookmarks/delete-bookmark-item-modal.tsx";
import DemoteBookmarkItemModal from "@via/pages/data/data-connections/bookmarks/demote-bookmark-item-modal.tsx";
import { ROUTES } from "@via/utils/constants.tsx";
import {
  NavigateFunction,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import BaseSpaceLogo from "/basespace-logo.png";
import {
  GlobeAltIcon,
  LockOpenIcon,
  ServerStackIcon,
} from "@heroicons/react/24/outline";
import { TitleLoader } from "@via/components/loading/loading.tsx";
import { classNames } from "@via/utils/helpers.tsx";

const providerTitlesMap: Record<DataSourceProvider, string> = {
  [DataSourceProvider.Amazon]: "Amazon Web Services",
  [DataSourceProvider.Google]: "Google Cloud",
  [DataSourceProvider.SSH]: "Clusters and Servers",
  [DataSourceProvider.URL]: "URL Sources",
  [DataSourceProvider.GEO]: "GEO Datasets",
  [DataSourceProvider.BaseSpace]: "BaseSpace",
  [DataSourceProvider.RemoteStorage]: "Remote servers",
};

const dataSourceHrefMap: Partial<Record<DataSourceProvider, string>> = {
  [DataSourceProvider.Amazon]: ROUTES.DATA_SOURCE_PROVIDER_AMAZON,
  [DataSourceProvider.Google]: ROUTES.DATA_SOURCE_PROVIDER_GOOGLE,
  [DataSourceProvider.SSH]: ROUTES.DATA_SOURCE_PROVIDER_SSH,
  [DataSourceProvider.BaseSpace]: ROUTES.DATA_SOURCE_PROVIDER_BASESPACE,
  [DataSourceProvider.URL]: ROUTES.DATA_SOURCE_PROVIDER_URL,
  [DataSourceProvider.RemoteStorage]:
    ROUTES.DATA_SOURCE_PROVIDER_REMOTE_STORAGE,
};

const getDataSourceHref = (
  dataSource: DataSource,
  datasetId?: string | null
): string => {
  const baseUrl = dataSourceHrefMap[dataSource.provider];
  if (!baseUrl) return "";

  const params = new URLSearchParams();
  if (dataSource?.id) {
    params.set("bookmark", dataSource.id.toString());
  }
  if (datasetId) {
    params.set("datasetId", datasetId);
  }

  const query = params.toString();
  return query ? `${baseUrl}?${query}` : baseUrl;
};

const logoMap: Partial<Record<DataSourceProvider, string>> = {
  [DataSourceProvider.Amazon]: S3Logo,
  [DataSourceProvider.Google]: GCSLogo,
  [DataSourceProvider.SSH]: ClusterServerLogo,
  [DataSourceProvider.BaseSpace]: BaseSpaceLogo,
};

const getDataSourceVerticalTabOptions = (
  dataSources: DataSource[],
  navigate: NavigateFunction,
  onDemote: (dataSourceId: number, name: string) => void,
  datasetId?: string | null
) =>
  [...dataSources]
    .sort((a, b) => a.provider - b.provider)
    .map(dataSource => {
      const { id: selectedDataSourceId, name, active, writable } = dataSource;
      return {
        id: selectedDataSourceId,
        name,
        customTitle: getCustomDataSourceTitle(dataSource),
        // Only on a source that is still writable: demotion is one way, so a read-only source has nothing to offer.
        // Shown to every reader, like delete; the server lets only the owner or an admin act.
        trailing: writable ? (
          <button
            className="mr-2"
            aria-label={`Make ${name} read-only`}
            title="Writable. Make read-only so runs on agent run environments can read from it"
            onClick={() => onDemote(selectedDataSourceId, name)}>
            <LockOpenIcon className="h-4 w-4 text-gray-400" />
          </button>
        ) : undefined,
        href: "",
        current: false,
        onClick: () => {
          if (active) {
            navigate(getDataSourceHref(dataSource, datasetId));
          }
        },
      };
    });

const getCustomDataSourceTitle = (dataSource: DataSource) => {
  const logo = logoMap[dataSource.provider];

  return (
    <div className={dataSource.active ? "" : "opacity-50 cursor-default"}>
      <div className="flex items-center">
        {dataSource.provider === DataSourceProvider.URL ? (
          <GlobeAltIcon className="mr-2 w-5 h-5" />
        ) : dataSource.provider === DataSourceProvider.RemoteStorage ? (
          <ServerStackIcon className="mr-2 w-5 h-5" />
        ) : (
          logo && (
            <img
              className="mr-2 h-4 w-4"
              alt={`${dataSource.name} Logo`}
              src={logo}
            />
          )
        )}
        <span className="max-w-50 overflow-hidden text-ellipsis whitespace-nowrap font-semibold">
          <TruncatedText
            text={dataSource.name}
            id={`tooltip-cloud${dataSource.name}`}
            externalClassNames={dataSource.active ? "" : "cursor-default"}
          />
        </span>
      </div>
      <div
        className={classNames(
          "flex justify-start",
          dataSource.provider === DataSourceProvider.BaseSpace ? "hidden" : ""
        )}>
        <span className="max-w-50 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-gray-400">
          <TruncatedText
            text={dataSource.path}
            id={`tooltip-cloud${dataSource.path}`}
            externalClassNames={dataSource.active ? "" : "cursor-default"}
          />
        </span>
      </div>
    </div>
  );
};

const BookmarkList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const datasetId = searchParams.get("datasetId");
  const [toBeDeletedBookmarkItem, setToBeDeletedBookmarkItem] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const [toBeDemotedBookmarkItem, setToBeDemotedBookmarkItem] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const handleDeleteDataSource = (dataSourceId: number, name: string) => {
    setToBeDeletedBookmarkItem({ id: dataSourceId, name });
  };

  const handleDemoteDataSource = (dataSourceId: number, name: string) => {
    setToBeDemotedBookmarkItem({ id: dataSourceId, name });
  };

  const { data: dataSourceList, isLoading: isDataSourceListLoading } =
    useGetDataSourceList();

  const groupedDataSources = useMemo(() => {
    return (dataSourceList || []).reduce<
      Record<DataSourceProvider, DataSource[]>
    >(
      (acc, ds) => {
        if (!acc[ds.provider]) acc[ds.provider] = [];
        acc[ds.provider].push(ds);
        return acc;
      },
      {} as Record<DataSourceProvider, DataSource[]>
    );
  }, [dataSourceList]);

  return (
    <div>
      <div className="w-full text-md font-bold">
        <span>Bookmarks</span>
      </div>
      {isDataSourceListLoading ? (
        <div className="flex flex-col mt-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <TitleLoader key={i} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-2 mt-2">
          {Object.entries(groupedDataSources)
            .filter(([, list]) => list.length > 0)
            .map(([provider, list]) => {
              const providerKey = Number(provider) as DataSourceProvider;
              const options = getDataSourceVerticalTabOptions(
                list,
                navigate,
                handleDemoteDataSource,
                datasetId
              );
              const title = providerTitlesMap[providerKey] ?? "Other";

              return (
                <VerticalTabs
                  key={provider}
                  title={title}
                  options={options}
                  onDelete={handleDeleteDataSource}
                />
              );
            })}
        </div>
      )}
      <DeleteBookmarkItemModal
        bookmarkedItem={toBeDeletedBookmarkItem}
        setBookmarkedItem={setToBeDeletedBookmarkItem}
      />
      <DemoteBookmarkItemModal
        bookmarkedItem={toBeDemotedBookmarkItem}
        setBookmarkedItem={setToBeDemotedBookmarkItem}
      />
    </div>
  );
};

export default BookmarkList;
